import { Check, ChevronDown, EyeOff, MapPin, Search } from "lucide-react";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MapLocation, MapStand } from "../shared/types";
import { VenueMapThumbnail } from "../map/VenueMapThumbnail";
import type { VenueMapLogos } from "../map/useVenueMapLogos";

function searchable(value: string) {
  return value
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

interface PickerLocation {
  id: string;
  label: string;
  hidden: boolean;
  stand?: MapStand;
}

/** One draft selection for booths and standalone places. */
export function LocationPicker({
  stands,
  locations,
  selectedId,
  logos,
  disabled,
  onSelect,
}: {
  stands: MapStand[];
  locations: MapLocation[];
  selectedId: string;
  logos: VenueMapLogos;
  disabled: boolean;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("");
  const [popupLayout, setPopupLayout] = useState({ above: false, height: 360 });
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const listId = `location-picker-${useId().replace(/:/g, "")}`;
  const entries = useMemo<PickerLocation[]>(() => {
    const standIds = new Set(stands.map((stand) => stand.id));
    return [
      ...stands.map((stand) => ({
        id: stand.id,
        label: stand.name || "Unassigned",
        hidden: !!stand.hidden,
        stand,
      })),
      ...locations
        .filter((location) => !standIds.has(location.id))
        .map((location) => ({
          id: location.id,
          label: location.label,
          hidden: !!location.hidden,
        })),
    ];
  }, [stands, locations]);
  const selected = entries.find((entry) => entry.id === selectedId);
  const matches = useMemo(() => {
    const words = searchable(query.trim()).split(/\s+/).filter(Boolean);
    return entries.filter((entry) => {
      const text = searchable(
        `${entry.id} ${entry.label} ${entry.stand?.shortName || ""} ${entry.stand?.mapLabel || ""}`,
      );
      return words.every((word) => text.includes(word));
    });
  }, [entries, query]);
  const activeIndex = matches.findIndex((stand) => stand.id === active);
  const expanded = open && !disabled;

  function close(restoreFocus = false) {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus({ preventScroll: true });
  }
  function choose(id: string) {
    close(true);
    onSelect(id);
  }
  function show() {
    setQuery("");
    setActive(selectedId);
    setOpen(true);
  }
  useLayoutEffect(() => {
    if (!expanded) return;
    const measure = () => {
      const bounds = trigger.current?.getBoundingClientRect();
      if (!bounds) return;
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop ?? 0;
      const bottom = top + (viewport?.height ?? window.innerHeight);
      const above = Math.max(0, bounds.top - top - 16);
      const below = Math.max(0, bottom - bounds.bottom - 16);
      const placeAbove = below < 320 && above > below;
      const height = Math.min(360, placeAbove ? above : below);
      setPopupLayout((previous) =>
        previous.above === placeAbove && previous.height === height
          ? previous
          : { above: placeAbove, height },
      );
    };
    measure();
    input.current?.focus({ preventScroll: true });
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    window.visualViewport?.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("scroll", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      window.visualViewport?.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("scroll", measure);
    };
  }, [expanded]);
  useLayoutEffect(() => {
    setOpen(false);
  }, [selectedId, disabled]);
  useEffect(() => {
    if (!expanded) return;
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [expanded]);
  useEffect(() => {
    if (!expanded) return;
    const option = list.current?.children[activeIndex] as
      HTMLElement | undefined;
    const results = list.current;
    if (!option || !results) return;
    // Scroll only the options, never the page or the editor behind the popup.
    if (option.offsetTop < results.scrollTop)
      results.scrollTop = option.offsetTop;
    else if (
      option.offsetTop + option.offsetHeight >
      results.scrollTop + results.clientHeight
    )
      results.scrollTop =
        option.offsetTop + option.offsetHeight - results.clientHeight;
  }, [expanded, activeIndex, query]);

  const locationContents = (entry: PickerLocation) => (
    <>
      <span className="em-booth-result-preview" aria-hidden="true">
        {entry.stand ? (
          <VenueMapThumbnail
            stand={entry.stand}
            label={entry.label}
            logos={logos}
          />
        ) : (
          <span className="em-location-result-icon">
            <MapPin size={22} />
          </span>
        )}
      </span>
      {entry.stand && <span className="em-booth-result-id">{entry.id}</span>}
      <span className="em-booth-result-name">
        <span>{entry.label}</span>
        <small>{entry.stand ? "Booth" : `Place · ${entry.id}`}</small>
      </span>
      {entry.hidden && (
        <span className="em-hidden-status">
          <EyeOff size={12} aria-hidden="true" />
          Hidden
        </span>
      )}
    </>
  );

  return (
    <div
      ref={root}
      className="em-booth-picker"
      onBlur={(event) => {
        if (!root.current?.contains(event.relatedTarget as Node | null))
          close();
      }}
    >
      <button
        ref={trigger}
        type="button"
        className="em-booth-picker-trigger"
        data-location-id={selectedId || undefined}
        aria-haspopup="listbox"
        aria-expanded={expanded}
        aria-controls={expanded ? listId : undefined}
        disabled={disabled}
        onClick={() => (expanded ? close() : show())}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            show();
          }
        }}
      >
        {selected ? (
          <>
            <span className="em-sr-only">Choose a location: </span>
            {locationContents(selected)}
          </>
        ) : (
          <>
            <Search size={17} aria-hidden="true" />
            <span className="em-booth-picker-placeholder">
              Choose a location
            </span>
          </>
        )}
        <ChevronDown
          size={17}
          className="em-booth-picker-chevron"
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <div
          className="em-booth-picker-popover"
          data-above={popupLayout.above}
          style={{ maxHeight: `${popupLayout.height}px` }}
        >
          <label className="em-search em-search-full">
            <Search size={17} aria-hidden="true" />
            <span className="em-sr-only">Search booths and places</span>
            <input
              ref={input}
              type="search"
              role="combobox"
              list={undefined}
              aria-autocomplete="list"
              aria-expanded="true"
              aria-controls={listId}
              aria-activedescendant={
                activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
              }
              placeholder="Find a booth or place…"
              autoComplete="off"
              spellcheck={false}
              value={query}
              onChange={(event) => {
                setQuery(event.currentTarget.value);
                setActive("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  event.stopPropagation();
                  close(true);
                } else if (
                  event.key === "ArrowDown" ||
                  event.key === "ArrowUp"
                ) {
                  event.preventDefault();
                  if (matches.length) {
                    const nextIndex =
                      event.key === "ArrowDown"
                        ? (activeIndex + 1) % matches.length
                        : activeIndex <= 0
                          ? matches.length - 1
                          : activeIndex - 1;
                    setActive(matches[nextIndex].id);
                  }
                } else if (
                  (event.key === "Home" || event.key === "End") &&
                  !query
                ) {
                  event.preventDefault();
                  if (matches.length)
                    setActive(
                      matches[event.key === "Home" ? 0 : matches.length - 1].id,
                    );
                } else if (event.key === "Enter") {
                  event.preventDefault();
                  if (matches.length)
                    choose(matches[Math.max(0, activeIndex)].id);
                }
              }}
            />
          </label>
          <div
            ref={list}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-label="Location search results"
            className="em-booth-results"
          >
            {matches.map((entry, index) => (
              <button
                key={entry.id}
                type="button"
                role="option"
                id={`${listId}-${index}`}
                data-booth-id={entry.stand ? entry.id : undefined}
                data-location-id={entry.id}
                className="em-booth-result"
                tabIndex={-1}
                aria-selected={selectedId === entry.id}
                data-active={activeIndex === index}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(entry.id)}
              >
                {locationContents(entry)}
                {selectedId === entry.id && (
                  <Check size={15} aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
          <p className="em-booth-picker-summary" role="status">
            {matches.length
              ? `${matches.length} ${matches.length === 1 ? "location" : "locations"}`
              : entries.length
                ? "No locations match your search. Try a booth number or name."
                : "Import a map document or add a place to get started."}
          </p>
        </div>
      )}
    </div>
  );
}
